/**
 * Where the game's two siblings live, from the client's point of view (M7-03).
 *
 * The game is one of three origins: the world server it holds a socket to, the
 * API it asks "do I have a character yet", and the portal it sends a signed-out
 * visitor to. In production each is its own host (`game.`, `server.`, the bare
 * `pendrane.com`); in dev they are ports on localhost. Both are baked in at
 * build time from `VITE_*` env, exactly as the server URL already is — a static
 * bundle cannot read a runtime config file, and a rebuild is the price of an
 * origin move for a site that already redeploys to publish a post.
 */

/**
 * The API base, for the `/me` creation-gate check. Defaults to the dev API port
 * in development and the game's own origin in production, where the API is
 * reverse-proxied under the same host.
 */
export function apiUrl(): string {
  const configured = import.meta.env['VITE_API_URL'] as string | undefined;
  if (typeof configured === 'string' && configured.length > 0) return configured.replace(/\/$/, '');
  return import.meta.env.DEV ? 'http://localhost:2568' : `${location.protocol}//${location.host}`;
}

/**
 * The portal's sign-in page, where a signed-out visitor is sent to log in or
 * claim an invite (M7-02/M7-03). `null` when there is no portal configured — a
 * local `pnpm dev` without the web app running — in which case the client keeps
 * the old guest fallback rather than redirecting into a void.
 */
export function portalLoginUrl(): string | null {
  const configured = import.meta.env['VITE_PORTAL_URL'] as string | undefined;
  const base =
    typeof configured === 'string' && configured.length > 0
      ? configured
      : import.meta.env.DEV
        ? 'http://localhost:3000'
        : null;
  if (!base) return null;
  return `${base.replace(/\/$/, '')}/login`;
}
